import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  LogIn, RefreshCw, Mail, Calendar, Search, LogOut, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, Download, Trash2, MailOpen, X, Undo2, Radio,
  Send, RotateCcw, Trash, ArchiveX, MessageSquare, Sparkles, Plus, Settings2,
} from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { Field, inputClass } from '@/components/calcUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'ce_admin_token';
const PAGE_SIZE = 25;
const POLL_MS = 30_000;
const UNDO_MS = 5_000;

export default function AdminPage() {
  useSeo({
    title: 'Admin — Contact Messages',
    description: 'Private admin dashboard for CalculateEasy contact form submissions.',
    path: '/admin',
    robots: 'noindex,nofollow,noarchive',
  });

  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
  });
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(!!token);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    let alive = true;
    axios.get(`${API}/admin/check`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => { if (alive) { setAuthed(true); setChecking(false); } })
      .catch(() => {
        if (!alive) return;
        setAuthed(false); setChecking(false);
        try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
      });
    return () => { alive = false; };
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!token.trim()) { setLoginError('Token is required.'); return; }
    try {
      await axios.get(`${API}/admin/check`, { headers: { Authorization: `Bearer ${token.trim()}` } });
      try { sessionStorage.setItem(TOKEN_KEY, token.trim()); } catch { /* ignore */ }
      setToken(token.trim());
      setAuthed(true);
    } catch (err) {
      const detail = err?.response?.status === 401 ? 'Invalid token.' :
                     err?.response?.status === 503 ? 'Admin is not configured on the server.' :
                     'Could not verify token — try again.';
      setLoginError(detail);
    }
  };

  const handleLogout = () => {
    try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    setToken(''); setAuthed(false);
  };

  if (checking) {
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">Verifying…</div>;
  }

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
        <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-4">
          <LogIn className="w-6 h-6" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900">Admin sign in</h1>
        <p className="text-slate-600 mt-2 text-sm">Enter the admin token configured in <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">backend/.env</code>.</p>
        <form onSubmit={handleLogin} className="mt-6 space-y-4 bg-white border border-slate-100 rounded-2xl p-6">
          <Field label="Admin token" htmlFor="admin-token">
            <input
              id="admin-token"
              data-testid="admin-token-input"
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className={inputClass}
              placeholder="Paste your ADMIN_TOKEN"
            />
          </Field>
          {loginError && <div className="text-sm text-rose-600" role="alert" data-testid="admin-login-error">{loginError}</div>}
          <button type="submit" data-testid="admin-login-btn" className="w-full inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700">
            <LogIn className="w-4 h-4" /> Sign in
          </button>
        </form>
      </div>
    );
  }

  return <MessagesDashboard token={token} onLogout={handleLogout} />;
}

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function MessagesDashboard({ token, onLogout }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [unreadAll, setUnreadAll] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 350);
  const [readFilter, setReadFilter] = useState('all'); // 'all' | 'unread' | 'read'
  const [stateFilter, setStateFilter] = useState('active'); // 'active' | 'deleted'
  const [selected, setSelected] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingPurge, setConfirmingPurge] = useState(false);
  const [confirmingEmptyBin, setConfirmingEmptyBin] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const [undo, setUndo] = useState(null);
  const [lastPollAt, setLastPollAt] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyForm, setReplyForm] = useState({ subject: '', body: '' });
  const [replyStatus, setReplyStatus] = useState({ state: 'idle', error: null });
  const [flash, setFlash] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [newTpl, setNewTpl] = useState({ name: '', subject: '', body: '' });
  const [tplError, setTplError] = useState('');
  const [signature, setSignature] = useState('');
  const [signatureDraft, setSignatureDraft] = useState('');
  const [signatureStatus, setSignatureStatus] = useState({ state: 'idle', error: null });

  const isTrash = stateFilter === 'deleted';
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const reqSeqRef = useRef(0);
  const undoTimerRef = useRef(null);
  const flashTimerRef = useRef(null);

  const fetchPage = useCallback(async (nextSkip = 0, opts = {}) => {
    const { silent = false } = opts;
    const mySeq = ++reqSeqRef.current;
    if (!silent) { setLoading(true); setError(''); }
    try {
      const params = {
        limit: PAGE_SIZE,
        skip: nextSkip,
        state: stateFilter,
        ...(debouncedQuery ? { q: debouncedQuery } : {}),
        ...(stateFilter === 'active' && readFilter !== 'all' ? { read: readFilter } : {}),
      };
      const r = await axios.get(`${API}/admin/messages`, { headers: authHeader, params });
      if (reqSeqRef.current !== mySeq) return; // stale response, ignore
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setUnread(r.data.unread || 0);
      setUnreadAll(r.data.unread_all ?? r.data.unread ?? 0);
      setDeletedCount(r.data.deleted_count ?? 0);
      setSkip(nextSkip);
      setLastPollAt(Date.now());
    } catch (err) {
      if (reqSeqRef.current !== mySeq) return;
      if (!silent) setError(err?.response?.data?.detail || 'Failed to load messages.');
    } finally {
      if (reqSeqRef.current === mySeq && !silent) setLoading(false);
    }
  }, [authHeader, debouncedQuery, readFilter, stateFilter]);

  // Reset to page 0 when any filter changes
  useEffect(() => {
    setCheckedIds(new Set());
    setSelected(null);
    setReplyOpen(false);
    fetchPage(0);
  }, [debouncedQuery, readFilter, stateFilter, fetchPage]);

  // Live polling — pauses when tab is hidden
  useEffect(() => {
    let interval;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => { fetchPage(skip, { silent: true }); }, POLL_MS);
    };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    const visibilityHandler = () => {
      if (document.hidden) stop();
      else { fetchPage(skip, { silent: true }); start(); }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', visibilityHandler);
    return () => { document.removeEventListener('visibilitychange', visibilityHandler); stop(); };
  }, [fetchPage, skip]);

  // Auto-mark selected message as read (skip when viewing the trash)
  useEffect(() => {
    if (!selected || selected.read || isTrash) return;
    const id = selected.id;
    axios.post(`${API}/admin/messages/${id}/read`, {}, { headers: authHeader })
      .then(() => {
        setItems((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
        setUnread((u) => Math.max(0, u - 1));
        setUnreadAll((u) => Math.max(0, u - 1));
        setSelected((s) => (s && s.id === id ? { ...s, read: true } : s));
      })
      .catch(() => { /* soft-fail */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, isTrash]);

  // Clear timers on unmount
  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  const showFlash = (message, tone = 'success') => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlash({ message, tone });
    flashTimerRef.current = setTimeout(() => setFlash(null), 3500);
  };

  const scheduleUndo = (deletedItems) => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    const ids = deletedItems.map((m) => m.id);
    setUndo({ ids, count: ids.length, expiresAt: Date.now() + UNDO_MS });
    undoTimerRef.current = setTimeout(() => { setUndo(null); undoTimerRef.current = null; }, UNDO_MS);
  };

  const handleUndo = async () => {
    if (!undo?.ids?.length) return;
    if (undoTimerRef.current) { clearTimeout(undoTimerRef.current); undoTimerRef.current = null; }
    const ids = undo.ids;
    setUndo(null);
    try {
      await axios.post(`${API}/admin/messages/restore`, { ids }, { headers: authHeader });
      fetchPage(skip, { silent: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Undo failed.');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await axios.delete(`${API}/admin/messages/${selected.id}`, { headers: authHeader });
      const removed = items.find((m) => m.id === selected.id);
      setItems((prev) => prev.filter((m) => m.id !== selected.id));
      setTotal((t) => Math.max(0, t - 1));
      if (removed && !removed.read) {
        setUnread((u) => Math.max(0, u - 1));
        setUnreadAll((u) => Math.max(0, u - 1));
      }
      setSelected(null);
      setConfirmingDelete(false);
      if (removed) scheduleUndo([removed]);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to delete message.');
    }
  };

  const handleExport = async () => {
    try {
      const r = await axios.get(`${API}/admin/messages.csv`, {
        headers: authHeader,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      a.download = `calculateeasy-messages-${now.toISOString().replace(/[:.]/g, '-').slice(0, 15)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to export.');
    }
  };

  const toggleCheck = (id, e) => {
    e?.stopPropagation?.();
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allOnPageChecked = items.length > 0 && items.every((m) => checkedIds.has(m.id));
  const someChecked = checkedIds.size > 0;
  const toggleAllOnPage = () => {
    setCheckedIds((prev) => {
      if (allOnPageChecked) {
        const next = new Set(prev);
        items.forEach((m) => next.delete(m.id));
        return next;
      }
      const next = new Set(prev);
      items.forEach((m) => next.add(m.id));
      return next;
    });
  };
  const clearChecks = () => setCheckedIds(new Set());

  const handleBulkRead = async () => {
    const ids = Array.from(checkedIds);
    if (!ids.length) return;
    try {
      await axios.post(`${API}/admin/messages/bulk-read`, { ids }, { headers: authHeader });
      const stillUnread = items.filter((m) => checkedIds.has(m.id) && !m.read).length;
      setItems((prev) => prev.map((m) => (checkedIds.has(m.id) ? { ...m, read: true } : m)));
      setUnread((u) => Math.max(0, u - stillUnread));
      setUnreadAll((u) => Math.max(0, u - stillUnread));
      clearChecks();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Bulk mark-read failed.');
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(checkedIds);
    if (!ids.length) return;
    const removed = items.filter((m) => checkedIds.has(m.id));
    try {
      await axios.post(`${API}/admin/messages/bulk-delete`, { ids }, { headers: authHeader });
      setBulkConfirmDelete(false);
      clearChecks();
      if (selected && ids.includes(selected.id)) setSelected(null);
      await fetchPage(Math.min(skip, Math.max(0, total - removed.length - 1)), { silent: true });
      if (removed.length) scheduleUndo(removed);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Bulk delete failed.');
    }
  };

  // ---- Trash-only actions ----
  const handleRestoreOne = async (id) => {
    try {
      await axios.post(`${API}/admin/messages/restore`, { ids: [id] }, { headers: authHeader });
      if (selected?.id === id) setSelected(null);
      await fetchPage(skip, { silent: true });
      showFlash('Message restored');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Restore failed.');
    }
  };

  const handleBulkRestore = async () => {
    const ids = Array.from(checkedIds);
    if (!ids.length) return;
    try {
      await axios.post(`${API}/admin/messages/restore`, { ids }, { headers: authHeader });
      clearChecks();
      await fetchPage(skip, { silent: true });
      showFlash(`${ids.length} message${ids.length === 1 ? '' : 's'} restored`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Bulk restore failed.');
    }
  };

  const handlePurgeOne = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/admin/messages/purge-ids`, { ids: [selected.id] }, { headers: authHeader });
      setConfirmingPurge(false);
      setSelected(null);
      await fetchPage(skip, { silent: true });
      showFlash('Message removed permanently');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Purge failed.');
    }
  };

  const handleBulkPurge = async () => {
    const ids = Array.from(checkedIds);
    if (!ids.length) return;
    try {
      await axios.post(`${API}/admin/messages/purge-ids`, { ids }, { headers: authHeader });
      setBulkConfirmDelete(false);
      clearChecks();
      if (selected && ids.includes(selected.id)) setSelected(null);
      await fetchPage(skip, { silent: true });
      showFlash(`${ids.length} message${ids.length === 1 ? '' : 's'} removed permanently`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Bulk purge failed.');
    }
  };

  const handleEmptyBin = async () => {
    try {
      const r = await axios.post(`${API}/admin/messages/purge`, {}, { headers: authHeader });
      const purged = r.data?.purged ?? 0;
      setConfirmingEmptyBin(false);
      clearChecks();
      setSelected(null);
      await fetchPage(0, { silent: true });
      showFlash(`Bin emptied — ${purged} message${purged === 1 ? '' : 's'} removed`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Empty bin failed.');
    }
  };

  // ---- Reply ----
  const loadTemplates = useCallback(async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/reply-templates`, { headers: authHeader }),
        axios.get(`${API}/admin/signature`, { headers: authHeader }),
      ]);
      setTemplates(Array.isArray(tRes.data) ? tRes.data : []);
      const sig = sRes?.data?.value || '';
      setSignature(sig);
      setSignatureDraft(sig);
      setTemplatesLoaded(true);
    } catch (err) {
      // soft-fail — templates + signature are nice-to-have
      setTemplatesLoaded(true);
    }
  }, [authHeader]);

  const handleSaveSignature = async () => {
    setSignatureStatus({ state: 'sending', error: null });
    try {
      const r = await axios.put(`${API}/admin/signature`, { value: signatureDraft }, { headers: authHeader });
      setSignature(r.data?.value ?? signatureDraft);
      setSignatureStatus({ state: 'idle', error: null });
      showFlash('Signature saved');
    } catch (err) {
      setSignatureStatus({ state: 'error', error: err?.response?.data?.detail || 'Could not save signature.' });
    }
  };

  const applyTemplate = (tpl) => {
    if (!selected || !tpl) return;
    const first = (selected.name || '').trim().split(/\s+/)[0] || 'there';
    const sub = (v) => (v || '')
      .replace(/\{first_name\}/gi, first)
      .replace(/\{name\}/gi, selected.name || first)
      .replace(/\{subject\}/gi, selected.subject || '');
    setReplyForm({
      subject: tpl.subject ? sub(tpl.subject) : (replyForm.subject || (selected.subject?.startsWith('Re: ') ? selected.subject : `Re: ${selected.subject || ''}`)),
      body: sub(tpl.body),
    });
    setReplyStatus({ state: 'idle', error: null });
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setTplError('');
    if (!newTpl.name.trim() || newTpl.body.trim().length < 5) {
      setTplError('Name and a body of at least 5 characters are required.');
      return;
    }
    try {
      const r = await axios.post(`${API}/admin/reply-templates`, {
        name: newTpl.name.trim(),
        subject: newTpl.subject.trim() || null,
        body: newTpl.body,
      }, { headers: authHeader });
      setTemplates((prev) => [...prev, r.data]);
      setNewTpl({ name: '', subject: '', body: '' });
      showFlash('Template saved');
    } catch (err) {
      setTplError(err?.response?.data?.detail || 'Could not save template.');
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/admin/reply-templates/${id}`, { headers: authHeader });
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      setTplError(err?.response?.data?.detail || 'Could not delete template.');
    }
  };

  const openReply = () => {
    if (!selected) return;
    setReplyForm({
      subject: selected.subject?.startsWith('Re: ') ? selected.subject : `Re: ${selected.subject || ''}`,
      body: '',
    });
    setReplyStatus({ state: 'idle', error: null });
    setReplyOpen(true);
    setManageOpen(false);
    if (!templatesLoaded) loadTemplates();
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!replyForm.subject.trim() || replyForm.body.trim().length < 5) {
      setReplyStatus({ state: 'error', error: 'Subject and a message of at least 5 characters are required.' });
      return;
    }
    setReplyStatus({ state: 'sending', error: null });
    try {
      await axios.post(`${API}/admin/messages/${selected.id}/reply`, {
        subject: replyForm.subject.trim(),
        body: replyForm.body,
      }, { headers: authHeader });
      setReplyStatus({ state: 'idle', error: null });
      setReplyOpen(false);
      showFlash(`Reply sent to ${selected.email}`);
      // Message is now marked read on server; sync local
      setItems((prev) => prev.map((m) => (m.id === selected.id ? { ...m, read: true } : m)));
      setSelected((s) => (s ? { ...s, read: true } : s));
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = 'Failed to send reply.';
      if (status === 503) msg = 'Resend is not configured on this server.';
      else if (typeof detail === 'string') msg = detail;
      setReplyStatus({ state: 'error', error: msg });
    }
  };

  const canPrev = skip > 0;
  const canNext = skip + PAGE_SIZE < total;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-32 relative">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900">Contact messages</h1>
            {unreadAll > 0 && (
              <span data-testid="admin-unread-badge" className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-600 text-white text-xs font-semibold">
                {unreadAll} unread
              </span>
            )}
          </div>
          <p className="text-slate-600 mt-1 text-sm flex items-center gap-2 flex-wrap" data-testid="admin-total">
            {loading ? 'Loading…' : (
              <>
                <span>
                  {debouncedQuery
                    ? `${total} match${total === 1 ? '' : 'es'} for "${debouncedQuery}"`
                    : `${total} message${total === 1 ? '' : 's'}${readFilter !== 'all' ? ` · ${readFilter}` : ' total'}`}
                </span>
                {lastPollAt && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400" title={new Date(lastPollAt).toLocaleTimeString()}>
                    <Radio className="w-3 h-3 text-emerald-500" /> auto-updating
                  </span>
                )}
              </>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isTrash && deletedCount > 0 && (
            !confirmingEmptyBin ? (
              <button onClick={() => setConfirmingEmptyBin(true)} data-testid="admin-empty-bin"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-rose-200 text-rose-700 text-sm font-medium hover:bg-rose-50">
                <ArchiveX className="w-4 h-4" /> Empty bin
              </button>
            ) : (
              <div className="inline-flex items-center gap-2" data-testid="admin-empty-bin-confirm">
                <span className="text-sm text-slate-700">Delete all {deletedCount} forever?</span>
                <button onClick={handleEmptyBin} data-testid="admin-empty-bin-yes"
                  className="px-3 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700">Yes</button>
                <button onClick={() => setConfirmingEmptyBin(false)} data-testid="admin-empty-bin-cancel"
                  className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
              </div>
            )
          )}
          <button onClick={handleExport} disabled={loading || total === 0 || isTrash} data-testid="admin-export"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={() => fetchPage(skip)} disabled={loading} data-testid="admin-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={onLogout} data-testid="admin-logout"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      {/* Read filter tabs */}
      <div className="flex gap-2 mb-4 flex-wrap" role="tablist" aria-label="Filter messages">
        {[
          { id: 'all', state: 'active', read: 'all', label: 'All', count: stateFilter === 'active' && readFilter === 'all' ? total : (total > 0 && !isTrash && readFilter === 'all' ? total : undefined) },
          { id: 'unread', state: 'active', read: 'unread', label: 'Unread', count: unreadAll },
          { id: 'read', state: 'active', read: 'read', label: 'Read', count: undefined },
          { id: 'deleted', state: 'deleted', read: 'all', label: 'Deleted', count: deletedCount },
        ].map((opt) => {
          const active = (opt.state === stateFilter) && (opt.state === 'deleted' || opt.read === readFilter);
          const badgeCount = opt.id === 'read' ? Math.max(0, (stateFilter === 'active' ? total : deletedCount) - unread) : opt.count;
          return (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => { setStateFilter(opt.state); setReadFilter(opt.read); }}
              data-testid={`admin-filter-${opt.id === 'deleted' ? 'state-deleted' : `read-${opt.read}`}`}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? (opt.id === 'deleted' ? 'bg-rose-600 text-white' : 'bg-slate-900 text-white')
                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {opt.id === 'deleted' && <ArchiveX className="w-3.5 h-3.5" />}
              {opt.label}
              {typeof badgeCount === 'number' && (
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  {badgeCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the entire inbox — name, email, subject, or message body…"
          data-testid="admin-filter"
          className={inputClass + ' pl-10 pr-10'}
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            data-testid="admin-filter-clear"
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {someChecked && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="admin-bulk-bar">
          <div className="text-sm text-emerald-900 font-medium" data-testid="admin-bulk-count">
            {checkedIds.size} selected
          </div>
          <div className="flex gap-2 flex-wrap">
            {isTrash ? (
              <>
                <button onClick={handleBulkRestore} data-testid="admin-bulk-restore"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-emerald-300 text-emerald-800 text-sm font-medium hover:bg-emerald-100">
                  <RotateCcw className="w-4 h-4" /> Restore
                </button>
                {!bulkConfirmDelete ? (
                  <button onClick={() => setBulkConfirmDelete(true)} data-testid="admin-bulk-purge"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-rose-300 text-rose-700 text-sm font-medium hover:bg-rose-50">
                    <Trash className="w-4 h-4" /> Delete forever
                  </button>
                ) : (
                  <div className="inline-flex items-center gap-2" data-testid="admin-bulk-purge-confirm">
                    <span className="text-sm text-slate-700">Permanently delete {checkedIds.size}?</span>
                    <button onClick={handleBulkPurge} data-testid="admin-bulk-purge-yes"
                      className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700">Yes</button>
                    <button onClick={() => setBulkConfirmDelete(false)} data-testid="admin-bulk-purge-cancel"
                      className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
                  </div>
                )}
              </>
            ) : (
              <>
                <button onClick={handleBulkRead} data-testid="admin-bulk-read"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-emerald-300 text-emerald-800 text-sm font-medium hover:bg-emerald-100">
                  <MailOpen className="w-4 h-4" /> Mark read
                </button>
                {!bulkConfirmDelete ? (
                  <button onClick={() => setBulkConfirmDelete(true)} data-testid="admin-bulk-delete"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-rose-300 text-rose-700 text-sm font-medium hover:bg-rose-50">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                ) : (
                  <div className="inline-flex items-center gap-2" data-testid="admin-bulk-delete-confirm">
                    <span className="text-sm text-slate-700">Delete {checkedIds.size}?</span>
                    <button onClick={handleBulkDelete} data-testid="admin-bulk-delete-yes"
                      className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700">Yes</button>
                    <button onClick={() => setBulkConfirmDelete(false)} data-testid="admin-bulk-delete-cancel"
                      className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
                  </div>
                )}
              </>
            )}
            <button onClick={clearChecks} data-testid="admin-bulk-clear"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-transparent text-slate-600 text-sm font-medium hover:bg-white/50">
              <X className="w-4 h-4" /> Clear
            </button>
          </div>
        </div>
      )}

      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-4 py-3 mb-4" role="alert" data-testid="admin-error">{error}</div>}

      {loading && !items.length ? (
        <div className="py-16 text-center text-slate-500">Loading messages…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-slate-500" data-testid="admin-empty">
          {debouncedQuery
            ? `No messages match "${debouncedQuery}".`
            : isTrash
              ? 'The bin is empty.'
              : readFilter === 'unread'
                ? 'No unread messages.'
                : readFilter === 'read'
                  ? 'No read messages.'
                  : 'No messages yet.'}
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
          <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden max-h-[70vh] flex flex-col">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50/60">
              <input
                type="checkbox"
                data-testid="admin-select-all"
                aria-label="Select all messages on this page"
                checked={allOnPageChecked}
                onChange={toggleAllOnPage}
                className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                {allOnPageChecked ? 'Deselect page' : 'Select page'}
              </span>
            </div>
            <ul className="divide-y divide-slate-100 overflow-y-auto">
              {items.map((m) => (
                <li key={m.id} data-testid={`admin-message-item-${m.id}`}>
                  <div className={`flex items-start gap-3 px-5 py-4 hover:bg-slate-50 transition-colors ${selected?.id === m.id ? 'bg-emerald-50' : ''}`}>
                    <input
                      type="checkbox"
                      data-testid={`admin-check-${m.id}`}
                      aria-label={`Select ${m.subject}`}
                      checked={checkedIds.has(m.id)}
                      onChange={(e) => toggleCheck(m.id, e)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 shrink-0"
                    />
                    <button
                      type="button"
                      onClick={() => setSelected(m)}
                      data-testid={`admin-message-${m.id}`}
                      className="flex-1 text-left min-w-0"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {!m.read && (
                            <span data-testid={`admin-unread-dot-${m.id}`} className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" title="Unread" />
                          )}
                          <div className={`truncate ${m.read ? 'font-medium text-slate-700' : 'font-semibold text-slate-900'}`}>{m.subject}</div>
                        </div>
                        <span title={m.email_sent ? 'Email delivered' : 'Email not sent'}>
                          {m.email_sent
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                            : <XCircle className="w-4 h-4 text-slate-300 shrink-0" />}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 mt-0.5 truncate">{m.name} · {m.email}</div>
                      <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {new Date(m.created_at).toLocaleString()}
                      </div>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl p-6 min-h-[400px]">
            {selected ? (
              <div className="space-y-4" data-testid="admin-message-detail">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">Subject</div>
                    <h2 className="text-xl font-semibold text-slate-900">{selected.subject}</h2>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    {!isTrash && !replyOpen && (
                      <button onClick={openReply} data-testid="admin-reply-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">
                        <MessageSquare className="w-3.5 h-3.5" /> Reply
                      </button>
                    )}
                    {isTrash ? (
                      <>
                        <button onClick={() => handleRestoreOne(selected.id)} data-testid="admin-restore-btn"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-emerald-300 text-emerald-700 text-sm font-medium hover:bg-emerald-50">
                          <RotateCcw className="w-3.5 h-3.5" /> Restore
                        </button>
                        {!confirmingPurge ? (
                          <button onClick={() => setConfirmingPurge(true)} data-testid="admin-purge-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-rose-300 text-rose-700 text-sm font-medium hover:bg-rose-50">
                            <Trash className="w-3.5 h-3.5" /> Delete forever
                          </button>
                        ) : (
                          <div className="flex items-center gap-2 text-sm" data-testid="admin-purge-confirm">
                            <span className="text-slate-600">Permanent?</span>
                            <button onClick={handlePurgeOne} data-testid="admin-purge-confirm-yes" className="px-3 py-1.5 rounded-lg bg-rose-600 text-white font-medium hover:bg-rose-700">Yes</button>
                            <button onClick={() => setConfirmingPurge(false)} data-testid="admin-purge-confirm-cancel" className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50">Cancel</button>
                          </div>
                        )}
                      </>
                    ) : !confirmingDelete ? (
                      <button onClick={() => setConfirmingDelete(true)} data-testid="admin-delete-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-600 text-sm font-medium hover:bg-rose-50">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 text-sm" data-testid="admin-delete-confirm">
                        <span className="text-slate-600">Delete?</span>
                        <button onClick={handleDelete} data-testid="admin-delete-confirm-yes" className="px-3 py-1.5 rounded-lg bg-rose-600 text-white font-medium hover:bg-rose-700">Yes</button>
                        <button onClick={() => setConfirmingDelete(false)} data-testid="admin-delete-confirm-cancel" className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50">Cancel</button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">From</div>
                    <div className="text-slate-900 font-medium">{selected.name}</div>
                    <a href={`mailto:${selected.email}`} className="text-emerald-700 text-sm inline-flex items-center gap-1 hover:underline">
                      <Mail className="w-3.5 h-3.5" /> {selected.email}
                    </a>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">Received</div>
                    <div className="text-slate-900">{new Date(selected.created_at).toLocaleString()}</div>
                    <div className={`text-xs mt-0.5 ${selected.email_sent ? 'text-emerald-600' : 'text-slate-500'}`}>
                      {selected.email_sent ? 'Notification delivered' : 'Notification not sent'}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Message</div>
                  <div className="text-slate-700 whitespace-pre-wrap leading-relaxed text-sm bg-slate-50 rounded-lg p-4 border border-slate-100">
                    {selected.message}
                  </div>
                </div>

                {/* Reply thread history */}
                {Array.isArray(selected.replies) && selected.replies.length > 0 && (
                  <div className="pt-2">
                    <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Replies sent</div>
                    <ul className="space-y-2" data-testid="admin-reply-history">
                      {selected.replies.map((r, i) => (
                        <li key={i} className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3 text-sm">
                          <div className="flex items-center justify-between text-xs text-emerald-800">
                            <span>{r.subject}</span>
                            <span>{r.sent_at ? new Date(r.sent_at).toLocaleString() : ''}</span>
                          </div>
                          <div className="mt-1.5 text-slate-700 whitespace-pre-wrap leading-relaxed">{r.body}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Reply composer */}
                {replyOpen && !isTrash && (
                  <form onSubmit={handleSendReply} className="pt-2 border-t border-slate-100 space-y-3" data-testid="admin-reply-composer">
                    <div className="flex items-center justify-between">
                      <div className="text-xs uppercase tracking-wide text-slate-400">Reply to {selected.email}</div>
                      <button type="button" onClick={() => setReplyOpen(false)} data-testid="admin-reply-cancel" className="text-slate-400 hover:text-slate-700">
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Template chips */}
                    <div data-testid="admin-reply-templates">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3" /> Templates
                        </div>
                        <button
                          type="button"
                          onClick={() => setManageOpen((v) => !v)}
                          data-testid="admin-reply-templates-manage"
                          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                        >
                          <Settings2 className="w-3 h-3" /> {manageOpen ? 'Close' : 'Manage'}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {templates.length === 0 && templatesLoaded && (
                          <div className="text-xs text-slate-400">No templates yet. Click Manage to create one.</div>
                        )}
                        {templates.map((tpl) => (
                          <button
                            key={tpl.id}
                            type="button"
                            onClick={() => applyTemplate(tpl)}
                            data-testid={`admin-reply-template-${tpl.id}`}
                            title={tpl.body}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-emerald-100 text-slate-700 hover:text-emerald-800 text-xs font-medium border border-transparent hover:border-emerald-200 transition-colors"
                          >
                            <Plus className="w-3 h-3" /> {tpl.name}
                          </button>
                        ))}
                      </div>

                      {manageOpen && (
                        <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50/60 p-4 space-y-3" data-testid="admin-templates-manager">
                          {templates.length > 0 && (
                            <ul className="space-y-2 max-h-48 overflow-y-auto">
                              {templates.map((tpl) => (
                                <li key={tpl.id} className="flex items-start gap-3 bg-white border border-slate-100 rounded-lg px-3 py-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-slate-800 truncate">{tpl.name}</div>
                                    <div className="text-xs text-slate-500 truncate whitespace-pre-wrap">{tpl.body.slice(0, 100)}{tpl.body.length > 100 ? '…' : ''}</div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTemplate(tpl.id)}
                                    data-testid={`admin-template-delete-${tpl.id}`}
                                    aria-label={`Delete template ${tpl.name}`}
                                    className="text-slate-400 hover:text-rose-600 shrink-0"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          <div className="pt-2 border-t border-slate-200 space-y-2">
                            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">New template</div>
                            <div className="grid sm:grid-cols-2 gap-2">
                              <input
                                data-testid="admin-template-new-name"
                                placeholder="Name (e.g. Thanks)"
                                value={newTpl.name}
                                onChange={(e) => setNewTpl((t) => ({ ...t, name: e.target.value }))}
                                className={inputClass + ' h-10'}
                              />
                              <input
                                data-testid="admin-template-new-subject"
                                placeholder="Subject override (optional)"
                                value={newTpl.subject}
                                onChange={(e) => setNewTpl((t) => ({ ...t, subject: e.target.value }))}
                                className={inputClass + ' h-10'}
                              />
                            </div>
                            <textarea
                              data-testid="admin-template-new-body"
                              rows={3}
                              placeholder={'Body — use {first_name}, {name}, {subject} placeholders'}
                              value={newTpl.body}
                              onChange={(e) => setNewTpl((t) => ({ ...t, body: e.target.value }))}
                              className={inputClass + ' h-auto py-2 text-sm'}
                            />
                            {tplError && <div className="text-xs text-rose-600" role="alert" data-testid="admin-template-error">{tplError}</div>}
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={handleCreateTemplate}
                                data-testid="admin-template-save"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-700"
                              >
                                <Plus className="w-3.5 h-3.5" /> Save template
                              </button>
                            </div>
                            <div className="text-xs text-slate-500">
                              Placeholders substituted at send time: <code className="font-mono">{'{first_name}'}</code>, <code className="font-mono">{'{name}'}</code>, <code className="font-mono">{'{subject}'}</code>.
                            </div>
                          </div>

                          {/* Signature editor */}
                          <div className="pt-3 border-t border-slate-200 space-y-2" data-testid="admin-signature-editor">
                            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Signature</div>
                            <textarea
                              data-testid="admin-signature-input"
                              rows={3}
                              placeholder={'Best,\nYour name — CalculateEasy'}
                              value={signatureDraft}
                              onChange={(e) => setSignatureDraft(e.target.value)}
                              className={inputClass + ' h-auto py-2 text-sm'}
                            />
                            {signatureStatus.error && <div className="text-xs text-rose-600" role="alert" data-testid="admin-signature-error">{signatureStatus.error}</div>}
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-slate-500">Appended to every reply after a <code className="font-mono">--</code> separator. Save empty to disable.</div>
                              <button
                                type="button"
                                onClick={handleSaveSignature}
                                disabled={signatureStatus.state === 'sending' || signatureDraft === signature}
                                data-testid="admin-signature-save"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-40"
                              >
                                <Send className="w-3.5 h-3.5" />
                                {signatureStatus.state === 'sending' ? 'Saving…' : 'Save signature'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <Field label="Subject" htmlFor="admin-reply-subject">
                      <input
                        id="admin-reply-subject"
                        data-testid="admin-reply-subject"
                        value={replyForm.subject}
                        onChange={(e) => setReplyForm((f) => ({ ...f, subject: e.target.value }))}
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Message" htmlFor="admin-reply-body">
                      <textarea
                        id="admin-reply-body"
                        data-testid="admin-reply-body"
                        rows={6}
                        value={replyForm.body}
                        onChange={(e) => setReplyForm((f) => ({ ...f, body: e.target.value }))}
                        className={inputClass + ' h-auto py-3'}
                        placeholder={`Hi ${selected.name?.split(' ')[0] || 'there'}, thanks for reaching out…`}
                      />
                    </Field>
                    {signature.trim() && (
                      <div className="rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2 text-xs text-slate-500" data-testid="admin-signature-preview">
                        <div className="uppercase tracking-wide text-slate-400 font-semibold mb-1">Signed with</div>
                        <pre className="whitespace-pre-wrap font-sans text-slate-600 leading-relaxed">--{"\n"}{signature}</pre>
                      </div>
                    )}
                    {replyStatus.error && (
                      <div className="text-sm text-rose-600" role="alert" data-testid="admin-reply-error">{replyStatus.error}</div>
                    )}
                    <button
                      type="submit"
                      disabled={replyStatus.state === 'sending'}
                      data-testid="admin-reply-send"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60"
                    >
                      <Send className="w-4 h-4" />
                      {replyStatus.state === 'sending' ? 'Sending…' : 'Send reply'}
                    </button>
                  </form>
                )}
              </div>
            ) : (
              <div className="text-slate-400 text-sm h-full flex items-center justify-center">Select a message on the left to read it.</div>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between text-sm">
        <div className="text-slate-500" data-testid="admin-page-info">
          Showing {items.length ? skip + 1 : 0}–{skip + items.length} of {total}
        </div>
        <div className="flex gap-2">
          <button onClick={() => fetchPage(Math.max(0, skip - PAGE_SIZE))} disabled={!canPrev || loading}
            data-testid="admin-prev"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 disabled:opacity-40 hover:bg-slate-50">
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <button onClick={() => fetchPage(skip + PAGE_SIZE)} disabled={!canNext || loading}
            data-testid="admin-next"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 disabled:opacity-40 hover:bg-slate-50">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Undo toast */}
      {undo && (
        <div
          role="status"
          aria-live="polite"
          data-testid="admin-undo-toast"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 pl-5 pr-2 py-2.5 rounded-xl bg-slate-900 text-white shadow-xl shadow-slate-900/20 animate-in fade-in slide-in-from-bottom-2 duration-200"
        >
          <span className="text-sm">
            {undo.count === 1 ? 'Message deleted' : `${undo.count} messages deleted`}
          </span>
          <button
            type="button"
            onClick={handleUndo}
            data-testid="admin-undo-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold"
          >
            <Undo2 className="w-3.5 h-3.5" /> Undo
          </button>
        </div>
      )}

      {/* Flash toast (info/success) */}
      {flash && !undo && (
        <div
          role="status"
          aria-live="polite"
          data-testid="admin-flash-toast"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 rounded-xl bg-emerald-600 text-white shadow-xl shadow-emerald-900/20 text-sm animate-in fade-in slide-in-from-bottom-2 duration-200"
        >
          {flash.message}
        </div>
      )}
    </div>
  );
}
